import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import {
  IEmailTemplateDetail,
  IEmailTemplateSummary,
  IPublishEmailPayload,
  ISaveEmailDraftPayload,
  ITranslateEmailPayload,
} from "../../interfaces/emailTemplate.interface";
import {
  getEmailTemplateServer,
  getEmailTemplatesServer,
  publishEmailTemplateServer,
  saveEmailDraftServer,
  translateEmailTemplateServer,
} from "./emailTemplatesApi";
import { hasPendingDraft } from "./templateDiff";

const PENDING_LOAD_CONCURRENCY = 6;

export interface EmailTemplatesState {
  templates: IEmailTemplateSummary[];
  current: IEmailTemplateDetail | null;
  status: "idle" | "loading" | "failed";
  pending: Record<string, boolean>;
  pendingLoading: boolean;
}

const initialState: EmailTemplatesState = {
  templates: [],
  current: null,
  status: "idle",
  pending: {},
  pendingLoading: false,
};

export const getEmailTemplatesThunk = createAsyncThunk(
  "emailTemplates/getAll",
  async () => {
    return getEmailTemplatesServer();
  }
);

export const getEmailTemplateThunk = createAsyncThunk(
  "emailTemplates/getOne",
  async (slug: string) => {
    return getEmailTemplateServer(slug);
  }
);

export const saveEmailDraftThunk = createAsyncThunk(
  "emailTemplates/saveDraft",
  async (params: { slug: string; payload: ISaveEmailDraftPayload }) => {
    return saveEmailDraftServer(params);
  }
);

export const publishEmailTemplateThunk = createAsyncThunk(
  "emailTemplates/publish",
  async (params: { slug: string; payload: IPublishEmailPayload }) => {
    await publishEmailTemplateServer(params);
    return getEmailTemplateServer(params.slug);
  }
);

export const translateEmailTemplateThunk = createAsyncThunk(
  "emailTemplates/translate",
  async (params: { slug: string; payload: ITranslateEmailPayload }) => {
    return translateEmailTemplateServer(params);
  }
);

export const loadPendingStatesThunk = createAsyncThunk(
  "emailTemplates/loadPending",
  async (slugs: string[]) => {
    const result: Record<string, boolean> = {};
    let cursor = 0;
    const worker = async () => {
      while (cursor < slugs.length) {
        const slug = slugs[cursor];
        cursor += 1;
        try {
          const detail = await getEmailTemplateServer(slug);
          result[slug] = hasPendingDraft(detail);
        } catch {
          result[slug] = false;
        }
      }
    };
    await Promise.all(
      Array.from(
        { length: Math.min(PENDING_LOAD_CONCURRENCY, slugs.length) },
        worker
      )
    );
    return result;
  }
);

export const discardEmailDraftThunk = createAsyncThunk(
  "emailTemplates/discard",
  async (params: { slug: string }) => {
    const detail = await getEmailTemplateServer(params.slug);
    for (const [locale, content] of Object.entries(detail.published)) {
      await saveEmailDraftServer({
        slug: params.slug,
        payload: { locale, content },
      });
    }
    return getEmailTemplateServer(params.slug);
  }
);

export const emailTemplatesSlice = createSlice({
  name: "emailTemplates",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getEmailTemplatesThunk.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getEmailTemplatesThunk.fulfilled, (state, action) => {
        state.status = "idle";
        state.templates = action.payload;
      })
      .addCase(getEmailTemplatesThunk.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getEmailTemplateThunk.fulfilled, setCurrentAndPending)
      .addCase(saveEmailDraftThunk.fulfilled, setCurrentAndPending)
      .addCase(publishEmailTemplateThunk.fulfilled, setCurrentAndPending)
      .addCase(translateEmailTemplateThunk.fulfilled, setCurrentAndPending)
      .addCase(discardEmailDraftThunk.fulfilled, setCurrentAndPending)
      .addCase(loadPendingStatesThunk.pending, (state) => {
        state.pendingLoading = true;
      })
      .addCase(loadPendingStatesThunk.fulfilled, (state, action) => {
        state.pending = { ...state.pending, ...action.payload };
        state.pendingLoading = false;
      })
      .addCase(loadPendingStatesThunk.rejected, (state) => {
        state.pendingLoading = false;
      });
  },
});

function setCurrentAndPending(
  state: EmailTemplatesState,
  action: { payload: IEmailTemplateDetail }
) {
  state.current = action.payload;
  state.pending[action.payload.slug] = hasPendingDraft(action.payload);
}

export const selectEmailTemplates = (state: RootState) =>
  state.emailTemplates.templates;
export const selectCurrentEmailTemplate = (state: RootState) =>
  state.emailTemplates.current;
export const selectEmailTemplatesStatus = (state: RootState) =>
  state.emailTemplates.status;
export const selectEmailTemplatesPending = (state: RootState) =>
  state.emailTemplates.pending;
export const selectEmailTemplatesPendingLoading = (state: RootState) =>
  state.emailTemplates.pendingLoading;

export default emailTemplatesSlice.reducer;
